import {all, call, put, select, take, takeEvery, takeLatest} from 'redux-saga/effects'

import history from '../history'

import {
  GET_CURRENT_EVENT,
  GET_CURRENT_EVENT_FAILURE,
  GET_CURRENT_EVENT_SUCCESS,
  LOGIN,
  LOGIN_SUCCESS,
  LOGIN_FAILURE,
  PASSWORD_RESET_REQUEST,
  REGISTRATION,
  REGISTRATION_WITH_EXISTING_EMAIL,
  REGISTRATION_SUCCESS,
  getCurrentEvent,
  getCurrentEventFailure,
  getCurrentEventPending,
  getCurrentEventSuccess,
  getEventLooks,
  getSoonestEventDate,
  getUserFailure,
  getUserPending,
  getUserSuccess,
  login as loginAction,
  loginFailure,
  loginPending,
  loginSuccess,
  passwordResetRequestFailure,
  passwordResetRequestPending,
  passwordResetRequestSuccess,
  registrationExistingEmail,
  registrationFailure,
  registrationPending,
  registrationSuccess,
} from '../actions/api'
import {deactivateHelloBar} from '../actions/helloBar'
import {getAllEvents} from '../actions/events'
import {getFitProfile} from '../actions/fitProfile'
import {getParticipantLook} from '../actions/looks'
import {closeOnboarding} from '../components/Onboarding/redux/actions'
import {postOnboardingCompleteWorker} from '../components/Onboarding/redux/sagas'
import {INITIAL_PAGE_LOAD, reset} from '../actions/site'
import {getSizes} from '../actions/sizes'
import {
  LOGOUT,
  LOGOUT_TO_HOMEPAGE,
  REGISTRATION_FINISHED,
  USER_AND_EVENT_RECEIVED,
  USER_AND_EVENT_NOT_RECEIVED,
  userAndEventPending,
  userAndEventReceived,
  userAndEventNotReceived,
} from '../actions/user'
import {openErrorModal} from '../actions/userInteractions'
import {LoginError} from '../exceptions'
import {EventTypeNames} from '../store/enum'
import {EXISTING_EMAIL_WRONG_PASSWORD_ERROR} from '../const'
import {
  getLoginRedirectPath,
  isCurrentPageOnboardingStandalone,
} from '../components/Onboarding/standaloneUtils'
import * as api from '../api/api'
import * as auth from '../auth'
import * as cross from '../store/cross'
import * as events from '../store/events'
import * as lock from '../lock'
import * as optimizely from '../analytics/optimizely'
import * as track from '../track/track'
import * as URLS from '../urls'
import * as user from '../store/user'
import * as zendesk from '../zendesk'

// Login
// ----------------------------------------------------------------------------

export function* loginWorker({payload: {email, password, location}}) {
  try {
    yield put(loginPending())

    // pass the json from a successful response into the payload for the LOGIN_SUCCESS action
    // TODO: Really, it would be great to roll back login if we can't get the JWT, but this would require a second pass
    //  over this code that I don't have the time to do. The best we can do is wait for the JWT to come back.
    const {login} = yield all({
      login: call(api.login, email, password, location),
      jwt: call(api.getJwt, email, password),
    })

    const payload = api.onStatus({
      OK: ({json}) => json,
      400: ({json}) => {
        throw new LoginError('Invalid email or password.', json.errors)
      },
      DEFAULT: () => {
        throw new LoginError('Unknown error.', {
          email: 'Cannot complete login. Please refresh the page or contact customer support.',
          password: '',
        })
      },
    })(login)

    yield call(track.login, {...payload.analytics, location})
    yield put(loginSuccess(payload))
  } catch (e) {
    // turn the error into a LoginError if it's not one already
    const payload =
      e instanceof LoginError
        ? e
        : new LoginError(e.message, {
            email: 'An unexpected error occurred. Please try again or refresh the page.',
          })
    yield put(loginFailure(payload))
  }
}

export function* loginSaga() {
  yield takeLatest(LOGIN, loginWorker)
}

// Sign-up to Login
export function* regToLoginWorker({payload: {email, password, location}}) {
  yield put(loginAction(email, location, password))

  const {type} = yield take([LOGIN_SUCCESS, LOGIN_FAILURE])

  if (type === LOGIN_SUCCESS) {
    yield put(closeOnboarding())
    if (isCurrentPageOnboardingStandalone) {
      yield call(postOnboardingCompleteWorker)
    }
  } else {
    const errorMsg = new LoginError('Invalid password', {
      password: EXISTING_EMAIL_WRONG_PASSWORD_ERROR,
    })
    yield put(registrationFailure(errorMsg))
    window.location.href = getLoginRedirectPath()
  }
}

export function* regToLoginSaga() {
  yield takeLatest(REGISTRATION_WITH_EXISTING_EMAIL, regToLoginWorker)
}

// Registration
// ----------------------------------------------------------------------------

/**
 * Handle creating the `registration Client` tracking call properties. Participants
 * have event data on the server analytics object, so we take care not to overwrite
 * with blank keys (as they do not go through onboarding client-side). We must also
 * account for the static registration pages where no event info is obtained.
 *
 * @param {Object}           serverAnalytics Analytics object from the server
 * @param {Number|undefined} clientEventType The eventType from onboarding
 * @param {String|undefined} eventRole       The eventRole from onboarding
 * @param {String}           location        Where registration took place in the UI
 */
export function getRegistrationEventProperties(
  serverAnalytics,
  clientEventType,
  clientEventRole,
  location,
) {
  const eventProperties = {...serverAnalytics, location}

  if (!serverAnalytics.eventType && Number.isInteger(clientEventType)) {
    eventProperties.eventType = EventTypeNames[clientEventType]
  }

  if (!serverAnalytics.eventRole && clientEventRole) {
    eventProperties.eventRole = clientEventRole
  }

  return eventProperties
}

/**
 * To register a user, we must register them in the Django tables, then fetch
 * their JWT from the auth service. Contains logic to assist users who attempt
 * to register with existing email addresses.
 */
export function* registrationWorker({payload: {email, eventType, eventRole, password, location}}) {
  yield put(registrationPending())
  try {
    const response = yield call(api.register, email, password, location)
    const result = api.onStatus({
      OK: ({json}) => json,
      400: ({json}) => {
        throw new LoginError('Invalid email or password.', json.errors)
      },
      DEFAULT: () => {
        throw new LoginError('Unknown error.', {
          email:
            'Cannot complete registration. Please refresh the page or contact customer support.',
          password: '',
        })
      },
    })(response)

    // TODO: If this fails, we need a way to recover or inform the user. We can't register them again...
    yield call(api.getJwt, email, password)
    yield call(
      track.registration,
      getRegistrationEventProperties(result.analytics, eventType, eventRole, location),
    )
    yield call(zendesk.sendTags, 'Registration Success')
    yield put(registrationSuccess(result))
  } catch (e) {
    // turn the error into a LoginError if it's not one already (e.g. the getJwt call fails)
    const genericError = 'An unexpected error occurred. Please try again or refresh the page.'
    const payload = e instanceof LoginError ? e : new LoginError(e.message, {email: genericError})

    // Attempt to log the user in if they try registering with an existing email
    if (payload.email === 'This email has already been registered.') {
      yield put(registrationExistingEmail(email, location, password))

      const existingEmailFailurePayload =
        location === 'Static'
          ? new LoginError('Invalid Password', {
              password: EXISTING_EMAIL_WRONG_PASSWORD_ERROR,
            })
          : {}
      yield put(registrationFailure(existingEmailFailurePayload))
    } else {
      yield put(registrationFailure(payload))
    }
  }
}

export function* registrationSaga() {
  yield takeLatest(REGISTRATION, registrationWorker)
}

// Password Reset
// ----------------------------------------------------------------------------

export function* passwordResetWorker({payload: {email}}) {
  try {
    yield put(passwordResetRequestPending())

    const response = yield call(api.resetPassword, email)
    const payload = api.onStatus({
      OK: ({json}) => json,
      400: ({json}) => {
        throw new LoginError('Invalid email.', json.errors)
      },
      DEFAULT: () => {
        throw new LoginError('Unknown error.', {
          email:
            'Cannot complete password reset. Please refresh the page or contact customer support.',
        })
      },
    })(response)

    yield put(passwordResetRequestSuccess(payload))
  } catch (e) {
    // turn the error into a LoginError if it's not one already
    const payload =
      e instanceof LoginError
        ? e
        : new LoginError(e.message, {
            email: 'An unexpected error occurred. Please try again or refresh the page.',
          })
    yield put(passwordResetRequestFailure(payload))
  }
}

export function* passwordResetSaga() {
  yield takeLatest(PASSWORD_RESET_REQUEST, passwordResetWorker)
}

// Auth
// ----------------------------------------------------------------------------

/*
 * AUTH
 *
 * 00. take latest INITIAL_PAGE_LOAD, LOGIN_SUCCESS, or REGISTRATION_FINISHED
 *
 * HAPPY PATH
 * 01 check for JWT
      - if no JWT, GOTO 90
 * 02 GET to user service
 *    - 200, user is logged in GOTO 02
 *    - 401, JWT invalid GOTO 70
 *    - DEFAULT, error occurred GOTO 70
 * 03 save user {id, email, firstName, lastName} to user redux store
 * 03 GET to event service (passing user id)
 *    - 200, active event GOTO 04
 *    - 404, no active event GOTO 05
 *    - DEFAULT, error occurred GOTO 80
 * 04 save event {id, ..., orderId} to events.current redux store
 * 05 track.authenticatedUser()
 * 06 put USER_AND_EVENT_RECEIVED
 *
 * USER FAIL
 * 70 log error GOTO 90
 *
 * EVENT FAIL
 * 80 log error GOTO 05
 *
 * NOT LOGGED IN
 * 90 track.unauthenticatedUser()
 * 91 put USER_AND_EVENT_NOT_RECEIVED
 *
 */

const AUTH_MAP = {
  [INITIAL_PAGE_LOAD]: track.AUTH_TYPE.ALREADY_AUTHENTICATED,
  [LOGIN_SUCCESS]: track.AUTH_TYPE.LOGIN,
  [REGISTRATION_FINISHED]: track.AUTH_TYPE.REGISTRATION,
}

export function* getUserWorker() {
  yield put(getUserPending(user))
  try {
    const response = yield call(api.getUser)
    const result = api.onStatus({
      OK: ({json}) => json,
      401: () => {
        throw new Error('Could not get user. Invalid JWT.')
      },
      DEFAULT: () => {
        throw new Error('Could not get user. Unexpected error.')
      },
    })(response)
    yield put(getUserSuccess(result))
  } catch (e) {
    yield put(getUserFailure(e))
  }
}

export function* getCurrentEventWorker() {
  const userId = yield select(user.id)
  yield put(getCurrentEventPending(userId))
  try {
    const response = yield call(api.getCurrentEvent)
    const result = api.onStatus({
      OK: ({json}) => json,
      404: () => undefined, // user does not have a current event
      DEFAULT: () => {
        throw new Error('Could not get event. Unexpected error.')
      },
    })(response)
    yield put(getCurrentEventSuccess(result))
  } catch (e) {
    yield put(getCurrentEventFailure(e))
  }
}

export function* getCurrentEventWorkerSaga() {
  yield takeLatest(GET_CURRENT_EVENT, getCurrentEventWorker)
}

/**
 * Attempts to fetch the user and current event information if they have a JWT
 * stored in localStorage. If not, dispatches an action to indicate as such.
 */
export function* userAndEventWorker({type}) {
  yield put(userAndEventPending())
  try {
    const hasJwt = yield call(auth.hasJwt)
    if (hasJwt) {
      yield call(getUserWorker)
    }

    const isAuthenticated = yield select(user.isAuthenticated)
    if (!isAuthenticated) {
      yield call(track.unauthenticatedUser, {
        isHeadlessChromeUser: window.isHeadlessChromeUser,
        isLinuxUser: window.isLinuxUser,
        isPrerenderUser: window.isPrerenderUser,
        isWebPageTestUser: window.isWebPageTestUser,
      })
      yield put(userAndEventNotReceived())
      return
    }

    yield put(getCurrentEvent())
    const getCurrentEventAction = yield take([GET_CURRENT_EVENT_SUCCESS, GET_CURRENT_EVENT_FAILURE])
    if (getCurrentEventAction.type === GET_CURRENT_EVENT_FAILURE) {
      throw new Error()
    }

    const id = yield select(user.id)
    const email = yield select(user.email)
    const creatorType = yield select(cross.customerType)
    yield call(track.authenticatedUser, id, email, AUTH_MAP[type])
    yield call(optimizely.captureAudienceAttributes, 'customerType', creatorType)

    yield put(userAndEventReceived())
  } catch (e) {
    yield put(userAndEventNotReceived())
    yield put(openErrorModal())
  }
}

// we consume REGISTRATION_FINISHED instead of REGISTRATION_SUCCESS to
// allow for additional actions in between
export function* userAndEventSaga() {
  yield takeLatest([INITIAL_PAGE_LOAD, LOGIN_SUCCESS, REGISTRATION_FINISHED], userAndEventWorker)
}

// this is super useful!
// use like `const state = yield forUserAndEvent()` to delay your saga
//  until or while user and event information are received (or not)
// (returns `state` for your convenience)
export function* forUserAndEvent() {
  const isUserAndEventPending = yield select(user.isUserAndEventPending)
  if (isUserAndEventPending) {
    yield take([USER_AND_EVENT_RECEIVED, USER_AND_EVENT_NOT_RECEIVED])
  }
  return yield select()
}

// On User and Event Received
// ----------------------------------------------------------------------------

export function* onUserAndEventReceivedWorker() {
  const eventId = yield select(events.currentId)
  const userIsParticipant = yield select(cross.userIsParticipant)

  if (userIsParticipant) {
    yield put(getParticipantLook())
  } else if (eventId) {
    yield put(getEventLooks(eventId))
  }

  yield put(getAllEvents())
  yield put(getFitProfile())
  yield put(getSizes())
  yield call(zendesk.initialize)
}

export function* onUserAndEventReceivedSaga() {
  yield takeEvery(USER_AND_EVENT_RECEIVED, onUserAndEventReceivedWorker)
}

// Post Login & Registration
// ----------------------------------------------------------------------------

export function* postLoginAndRegistrationWorker({payload: {redirectToAccountEvents = false} = {}}) {
  const userIsParticipant = yield select(cross.userIsParticipant)

  if (userIsParticipant) {
    yield call(history.push, URLS.EVENT_SUMMARY)
  } else if (window.location.pathname === URLS.HOME && redirectToAccountEvents) {
    yield call(history.push, URLS.ACCOUNT_EVENTS)
  }
}

export function* postLoginAndRegistrationSaga() {
  yield takeLatest(
    [
      LOGIN_SUCCESS,
      // TODO: Could possibly change REGISTRATION_SUCCESS to REGISTRATION_FINISHED
      REGISTRATION_SUCCESS,
    ],
    postLoginAndRegistrationWorker,
  )
}

// Logout
// ----------------------------------------------------------------------------

export function* logoutWorker({type}) {
  // this redirect is before the redux store reset so we have some way to tell
  // that we're being pulled out of the current page, like in the fit flow
  // where we're checking to see that we're still in the fit flow when
  // running the SET_ON_DECK_STEP logic
  if (type === LOGOUT_TO_HOMEPAGE) {
    yield (window.location.href = URLS.HOME)
  }
  // reset the entire redux store
  yield put(reset())
  yield put(getSoonestEventDate())
  // unlock screen because this won't happen automatically when stack is reset
  // by the RESET action
  yield call(lock.unlock)
  yield call(auth.deleteJwt)

  // Revisit this logic - we may not want to deactivate all hello bars when a customer
  // logs out
  yield put(deactivateHelloBar())
}

export function* logoutSaga() {
  yield takeEvery([LOGOUT, LOGOUT_TO_HOMEPAGE], logoutWorker)
}
